/**
 * DemandLetterModal.js  —  chat-frontend
 *
 * Full-screen modal for reviewing, editing, and saving a demand letter.
 * Opened from ChatPanel when DRAFT_DEMAND_LETTER confirm is accepted.
 *
 * Props:
 *   recordId       — matter record UUID
 *   recordTitle    — display name
 *   onClose        — close handler
 *   onSaved        — called with { filename } after successful save
 */

import React, { useState, useCallback } from 'react';
import {
  X, Download, Save, FileText, ChevronDown, ChevronUp,
  Loader, AlertCircle, CheckCircle, Edit3, DollarSign
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '';

async function apiFetch(path, options = {}) {
  const token = localStorage.getItem('lf_token');
  const res   = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization:  `Bearer ${token}`,
      ...(options.headers || {})
    }
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res;
}

// ─── Section editor ────────────────────────────────────────────────────────────

function SectionBlock({ title, fieldKey, value, onChange, collapsible = false }) {
  const [open, setOpen] = useState(true);

  return (
    <div style={{
      marginBottom: 20,
      border: '1px solid #2a2a2a',
      borderRadius: 8,
      overflow: 'hidden'
    }}>
      <div
        onClick={() => collapsible && setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 14px',
          background: '#1a1a1a',
          cursor: collapsible ? 'pointer' : 'default',
          userSelect: 'none'
        }}
      >
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#c9a84c' }}>
          {title}
        </span>
        {collapsible && (open
          ? <ChevronUp size={14} color="#888" />
          : <ChevronDown size={14} color="#888" />
        )}
      </div>
      {open && (
        <textarea
          value={value || ''}
          onChange={e => onChange(fieldKey, e.target.value)}
          style={{
            width: '100%',
            minHeight: 120,
            padding: '12px 14px',
            background: '#0e0f11',
            color: '#e5e5e5',
            border: 'none',
            borderTop: '1px solid #2a2a2a',
            fontSize: 13,
            lineHeight: 1.7,
            fontFamily: '"Times New Roman", Times, serif',
            resize: 'vertical',
            boxSizing: 'border-box',
            outline: 'none'
          }}
        />
      )}
    </div>
  );
}

// ─── Main modal ────────────────────────────────────────────────────────────────

export default function DemandLetterModal({ recordId, recordTitle, onClose, onSaved }) {
  const [phase, setPhase]       = useState('idle');     // idle | generating | ready | saving | saved | error
  const [letter, setLetter]     = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [demandAmt, setDemandAmt] = useState('');
  const [editingAmt, setEditingAmt] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  // ── Generate ─────────────────────────────────────────────────────────────

  const handleGenerate = useCallback(async (overrideAmount = null) => {
    setPhase('generating');
    setErrorMsg('');
    try {
      const res  = await apiFetch('/api/demand-letter/generate', {
        method: 'POST',
        body:   JSON.stringify({ record_id: recordId, override_demand_amount: overrideAmount })
      });
      const data = await res.json();
      setLetter(data.letter);
      setDemandAmt(data.letter.suggested_demand_amount
        ? String(data.letter.suggested_demand_amount)
        : ''
      );
      setPhase('ready');
    } catch (err) {
      setErrorMsg(err.message);
      setPhase('error');
    }
  }, [recordId]);

  // ── Section edit ─────────────────────────────────────────────────────────

  const handleSectionChange = useCallback((key, value) => {
    setLetter(prev => ({
      ...prev,
      sections: { ...prev.sections, [key]: value }
    }));
  }, []);

  const handleRecipientChange = useCallback((key, value) => {
    setLetter(prev => ({
      ...prev,
      recipient: { ...prev.recipient, [key]: value }
    }));
  }, []);

  // ── Apply demand amount override ──────────────────────────────────────────

  const handleApplyDemandAmount = () => {
    const amt = parseFloat(demandAmt.replace(/[^0-9.]/g, ''));
    if (!isNaN(amt)) {
      setLetter(prev => ({ ...prev, suggested_demand_amount: amt }));
    }
    setEditingAmt(false);
  };

  const handleRegeneratWithAmount = () => {
    const amt = parseFloat(demandAmt.replace(/[^0-9.]/g, ''));
    if (!isNaN(amt)) {
      handleGenerate(amt);
    }
  };

  // ── Save to record ────────────────────────────────────────────────────────

  const handleSave = async () => {
    setPhase('saving');
    try {
      const res  = await apiFetch('/api/demand-letter/save', {
        method: 'POST',
        body:   JSON.stringify({ record_id: recordId, letter })
      });
      const data = await res.json();
      setPhase('saved');
      setSuccessMsg(`Saved: ${data.filename}`);
      if (onSaved) onSaved(data);
    } catch (err) {
      setErrorMsg(err.message);
      setPhase('error');
    }
  };

  // ── Download ──────────────────────────────────────────────────────────────

  const handleDownload = async (format) => {
    try {
      const token = localStorage.getItem('lf_token');
      const res   = await fetch(`${API_BASE}/api/demand-letter/download/${format}`, {
        method:  'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization:  `Bearer ${token}`
        },
        body: JSON.stringify({ letter })
      });
      if (!res.ok) throw new Error(`Download failed: HTTP ${res.status}`);
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `demand-letter-${letter.reference_number || 'draft'}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Download failed: ' + err.message);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20
    }}>
      <div style={{
        width: '100%', maxWidth: 820,
        maxHeight: '90vh',
        background: '#0e0f11',
        border: '1px solid #2a2a2a',
        borderRadius: 12,
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden'
      }}>

        {/* ── Header ── */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px',
          borderBottom: '1px solid #2a2a2a',
          background: '#111'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <FileText size={18} color="#c9a84c" />
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#f0f0f0' }}>Demand Letter</div>
              <div style={{ fontSize: 12, color: '#888' }}>{recordTitle}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <X size={20} color="#888" />
          </button>
        </div>

        {/* ── Body ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>

          {/* ── IDLE: Generate button ── */}
          {phase === 'idle' && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <FileText size={48} color="#c9a84c" style={{ marginBottom: 16, opacity: 0.7 }} />
              <div style={{ fontSize: 16, fontWeight: 600, color: '#e0e0e0', marginBottom: 8 }}>
                Draft Demand Letter
              </div>
              <div style={{ fontSize: 13, color: '#888', marginBottom: 28, maxWidth: 400, margin: '0 auto 28px' }}>
                Pulls all case data, medical records, parties, and documents on file to generate a professional PI demand letter.
              </div>
              <button onClick={() => handleGenerate()} style={{
                background: 'linear-gradient(135deg, #c9a84c, #a67c32)',
                color: '#000', border: 'none', borderRadius: 8,
                padding: '12px 28px', fontSize: 14, fontWeight: 700,
                cursor: 'pointer'
              }}>
                Generate Demand Letter
              </button>
            </div>
          )}

          {/* ── GENERATING ── */}
          {phase === 'generating' && (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <Loader size={36} color="#c9a84c" style={{ animation: 'spin 1s linear infinite', marginBottom: 16 }} />
              <div style={{ color: '#888', fontSize: 14 }}>Assembling case data and drafting letter…</div>
            </div>
          )}

          {/* ── ERROR ── */}
          {phase === 'error' && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <AlertCircle size={36} color="#ef4444" style={{ marginBottom: 16 }} />
              <div style={{ color: '#ef4444', fontSize: 14, marginBottom: 20 }}>{errorMsg}</div>
              <button onClick={() => { setPhase('idle'); setErrorMsg(''); }} style={{
                background: '#1a1a1a', color: '#e0e0e0', border: '1px solid #333',
                borderRadius: 8, padding: '10px 20px', cursor: 'pointer', fontSize: 13
              }}>
                Try Again
              </button>
            </div>
          )}

          {/* ── SAVED ── */}
          {phase === 'saved' && (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <CheckCircle size={36} color="#22c55e" style={{ marginBottom: 16 }} />
              <div style={{ color: '#22c55e', fontSize: 15, fontWeight: 600, marginBottom: 8 }}>
                Saved to Record
              </div>
              <div style={{ color: '#888', fontSize: 13, marginBottom: 24 }}>{successMsg}</div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                <button onClick={() => handleDownload('pdf')} style={{
                  background: '#1a1a1a', color: '#e0e0e0', border: '1px solid #333',
                  borderRadius: 8, padding: '10px 18px', cursor: 'pointer', fontSize: 13,
                  display: 'flex', alignItems: 'center', gap: 6
                }}>
                  <Download size={14} /> PDF
                </button>
                <button onClick={() => handleDownload('docx')} style={{
                  background: '#1a1a1a', color: '#e0e0e0', border: '1px solid #333',
                  borderRadius: 8, padding: '10px 18px', cursor: 'pointer', fontSize: 13,
                  display: 'flex', alignItems: 'center', gap: 6
                }}>
                  <Download size={14} /> DOCX
                </button>
              </div>
            </div>
          )}

          {/* ── READY: Editor ── */}
          {(phase === 'ready' || phase === 'saving') && letter && (
            <>
              {/* Demand amount bar */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12,
                background: '#111', border: '1px solid #2a2a2a', borderRadius: 10,
                padding: '12px 16px', marginBottom: 24
              }}>
                <DollarSign size={16} color="#c9a84c" />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>DEMAND AMOUNT</div>
                  {editingAmt ? (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        type="text"
                        value={demandAmt}
                        onChange={e => setDemandAmt(e.target.value)}
                        style={{
                          background: '#0e0f11', border: '1px solid #c9a84c', borderRadius: 6,
                          padding: '4px 10px', color: '#f0f0f0', fontSize: 15, fontWeight: 700,
                          width: 160, outline: 'none'
                        }}
                        placeholder="e.g. 150000"
                        autoFocus
                      />
                      <button onClick={handleApplyDemandAmount} style={{
                        background: '#1a2a1a', color: '#22c55e', border: '1px solid #22c55e',
                        borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 12
                      }}>Apply</button>
                      <button onClick={handleRegeneratWithAmount} style={{
                        background: '#1a1a2a', color: '#818cf8', border: '1px solid #818cf8',
                        borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 12
                      }}>Regenerate with this amount</button>
                      <button onClick={() => setEditingAmt(false)} style={{
                        background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: 12
                      }}>Cancel</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ fontSize: 18, fontWeight: 700, color: '#f0f0f0' }}>
                        {letter.suggested_demand_amount
                          ? `$${Number(letter.suggested_demand_amount).toLocaleString()}`
                          : 'Not set'}
                      </span>
                      {letter.demand_reasoning && (
                        <span style={{ fontSize: 12, color: '#888' }}>— {letter.demand_reasoning}</span>
                      )}
                      <button onClick={() => setEditingAmt(true)} style={{
                        background: 'none', border: '1px solid #333', borderRadius: 6,
                        padding: '3px 10px', cursor: 'pointer', color: '#c9a84c',
                        fontSize: 11, display: 'flex', alignItems: 'center', gap: 4
                      }}>
                        <Edit3 size={10} /> Override
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Recipient block */}
              <div style={{
                background: '#111', border: '1px solid #2a2a2a', borderRadius: 10,
                padding: 16, marginBottom: 24
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#c9a84c', marginBottom: 12 }}>
                  Recipient
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  {['name', 'title', 'company', 'address'].map(field => (
                    <div key={field} style={{ gridColumn: field === 'address' ? '1 / -1' : undefined }}>
                      <div style={{ fontSize: 10, color: '#666', marginBottom: 4, textTransform: 'capitalize' }}>{field}</div>
                      {field === 'address' ? (
                        <textarea
                          value={letter.recipient?.[field] || ''}
                          onChange={e => handleRecipientChange(field, e.target.value)}
                          rows={2}
                          style={{
                            width: '100%', background: '#0e0f11', border: '1px solid #2a2a2a',
                            borderRadius: 6, padding: '6px 10px', color: '#e0e0e0',
                            fontSize: 13, resize: 'vertical', outline: 'none', boxSizing: 'border-box'
                          }}
                        />
                      ) : (
                        <input
                          type="text"
                          value={letter.recipient?.[field] || ''}
                          onChange={e => handleRecipientChange(field, e.target.value)}
                          style={{
                            width: '100%', background: '#0e0f11', border: '1px solid #2a2a2a',
                            borderRadius: 6, padding: '6px 10px', color: '#e0e0e0',
                            fontSize: 13, outline: 'none', boxSizing: 'border-box'
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* RE line */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, color: '#666', marginBottom: 6 }}>RE LINE</div>
                <input
                  type="text"
                  value={letter.re_line || ''}
                  onChange={e => setLetter(prev => ({ ...prev, re_line: e.target.value }))}
                  style={{
                    width: '100%', background: '#0e0f11', border: '1px solid #2a2a2a',
                    borderRadius: 6, padding: '8px 12px', color: '#e0e0e0',
                    fontSize: 13, fontWeight: 600, outline: 'none', boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Letter sections */}
              {[
                { key: 'representation',         label: 'Representation' },
                { key: 'facts_of_incident',       label: 'Facts of the Incident' },
                { key: 'injuries_and_treatment',  label: 'Injuries and Medical Treatment' },
                { key: 'medical_specials',        label: 'Medical Specials' },
                { key: 'lost_wages',              label: 'Lost Wages' },
                { key: 'pain_and_suffering',      label: 'Pain and Suffering' },
                { key: 'demand',                  label: 'Demand' }
              ].map(({ key, label }) => (
                <SectionBlock
                  key={key}
                  title={label}
                  fieldKey={key}
                  value={letter.sections?.[key] || ''}
                  onChange={handleSectionChange}
                  collapsible
                />
              ))}
            </>
          )}
        </div>

        {/* ── Footer actions ── */}
        {(phase === 'ready' || phase === 'saving') && letter && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 20px',
            borderTop: '1px solid #2a2a2a',
            background: '#111',
            gap: 10
          }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => handleDownload('pdf')}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  background: '#1a1a1a', color: '#e0e0e0',
                  border: '1px solid #333', borderRadius: 8,
                  padding: '8px 16px', fontSize: 13, cursor: 'pointer'
                }}
              >
                <Download size={14} /> PDF
              </button>
              <button
                onClick={() => handleDownload('docx')}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  background: '#1a1a1a', color: '#e0e0e0',
                  border: '1px solid #333', borderRadius: 8,
                  padding: '8px 16px', fontSize: 13, cursor: 'pointer'
                }}
              >
                <Download size={14} /> DOCX
              </button>
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={onClose} style={{
                background: 'none', color: '#888', border: '1px solid #333',
                borderRadius: 8, padding: '8px 16px', fontSize: 13, cursor: 'pointer'
              }}>
                Cancel
              </button>
              <button
                onClick={() => handleSave()}
                disabled={phase === 'saving'}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  background: 'linear-gradient(135deg, #c9a84c, #a67c32)',
                  color: '#000', border: 'none', borderRadius: 8,
                  padding: '8px 20px', fontSize: 13, fontWeight: 700,
                  cursor: phase === 'saving' ? 'not-allowed' : 'pointer',
                  opacity: phase === 'saving' ? 0.7 : 1
                }}
              >
                {phase === 'saving'
                  ? <><Loader size={14} style={{ animation: 'spin 1s linear infinite' }} /> Saving…</>
                  : <><Save size={14} /> Save to Record</>
                }
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
